import { useQuery } from "@tanstack/react-query";
import { Loader2, Briefcase, CheckCircle2, Coins, Store, Wallet, Mail, XCircle } from "lucide-react";
import { adminApi, calcFee, withTax, type CaseRow } from "@/lib/api";

/** 問い合わせ窓口（info@）。実アドレスは運用で確定。導線のみ提供。 */
const INFO_EMAIL = "info@sirasu.jp";

/** 案件ステータスの表示ラベル。 */
const CASE_STATUS_LABEL: Record<string, string> = {
  draft: "下書き",
  open: "入札受付中",
  awarded: "業者確定",
  scheduled: "工事予定",
  completed: "完了",
  paid: "決済済",
  cancelled: "キャンセル",
};
const AWARDED_STATES = ["awarded", "scheduled", "completed", "paid"];

export function DashboardPage() {
  const { data: cases } = useQuery({ queryKey: ["all-cases"], queryFn: () => adminApi.listCases() });
  const { data: vendors } = useQuery({ queryKey: ["vendors"], queryFn: () => adminApi.listVendors() });
  const { data: payments } = useQuery({ queryKey: ["payments"], queryFn: () => adminApi.listPayments() });

  if (!cases || !vendors) {
    return <div className="px-4 py-10 flex justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const open = cases.filter((c) => c.status === "open").length;
  const awarded = cases.filter((c) => AWARDED_STATES.includes(c.status));
  const cancelled = cases.filter((c) => c.status === "cancelled");
  const gmv = awarded.reduce((s, c) => s + withTax(c.awarded_amount ?? 0), 0);
  const feeTotal = awarded.reduce((s, c) => s + calcFee(c.awarded_amount ?? 0), 0);
  const approvedVendors = vendors.filter((v) => v.status === "approved").length;
  const pendingVendors = vendors.filter((v) => v.status === "pending").length;

  // 決済テーブルが整備済みなら実データ、無ければ案件成約から推定。
  const pay = payments ?? [];
  const held = pay.filter((p) => p.status === "held");
  const released = pay.filter((p) => p.status === "released");
  const heldAmount = held.reduce((s, p) => s + (p.amount ?? 0), 0);
  const releasedPayout = released.reduce((s, p) => s + (p.vendor_payout ?? 0), 0);

  const statusCounts = countByStatus(cases);
  const series = monthlyFeeSeries(awarded, 6);

  return (
    <div className="px-4 py-4 space-y-4">
      <h2 className="text-lg font-semibold">ダッシュボード</h2>

      {/* 流通量・取引状況 */}
      <section className="space-y-2">
        <h3 className="text-sm font-bold text-ink-soft dark:text-white/70">流通量・取引状況</h3>
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={<Briefcase size={18} />} label="入札受付中" value={`${open}件`} />
          <Stat icon={<CheckCircle2 size={18} />} label="成約案件" value={`${awarded.length}件`} />
          <Stat icon={<Coins size={18} />} label="流通総額(GMV・税込)" value={`¥${gmv.toLocaleString("ja-JP")}`} />
          <Stat icon={<Coins size={18} />} label="手数料Revenue(推定)" value={`¥${feeTotal.toLocaleString("ja-JP")}`} accent />
          <Stat icon={<Store size={18} />} label="承認業者" value={`${approvedVendors}社`} />
          <Stat icon={<Store size={18} />} label="審査待ち" value={`${pendingVendors}社`} />
        </div>
      </section>

      {/* 決済（収納代行型）状態 */}
      <section className="space-y-2">
        <h3 className="text-sm font-bold text-ink-soft dark:text-white/70">決済（収納代行型）</h3>
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={<Wallet size={18} />} label="預り中(held)" value={`${held.length}件 / ¥${heldAmount.toLocaleString("ja-JP")}`} />
          <Stat icon={<Wallet size={18} />} label="業者振分済(released)" value={`${released.length}件 / ¥${releasedPayout.toLocaleString("ja-JP")}`} />
        </div>
        <p className="text-[11px] text-ink-soft dark:text-white/50">
          顧客 → 支良洲工業が収納（held）→ 顧客の受け取り確認＋レビューで業者へ振分（released）。
        </p>
      </section>

      {/* ステータス別 内訳 */}
      <section className="rounded-xl border border-[var(--color-border)] p-4 space-y-2">
        <h3 className="text-sm font-bold">案件ステータス別 内訳</h3>
        <ul className="divide-y divide-[var(--color-border)]">
          {Object.entries(CASE_STATUS_LABEL).map(([k, label]) => (
            <li key={k} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-ink-soft dark:text-white/70">{label}</span>
              <span className="font-semibold tabular-nums">{statusCounts[k] ?? 0}件</span>
            </li>
          ))}
        </ul>
      </section>

      <MonthlyRevenueChart series={series} />

      {/* キャンセル一覧（受注後に連絡途絶ケースを想定） */}
      <section className="rounded-xl border border-red-200 dark:border-red-900/50 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <XCircle size={16} className="text-red-500" />
          <h3 className="text-sm font-bold">キャンセル案件（{cancelled.length}件）</h3>
        </div>
        <p className="text-[11px] text-ink-soft dark:text-white/50">
          受注後にチャットで連絡が途絶した等のキャンセル。キャンセルポリシーに基づき対応。
        </p>
        {cancelled.length === 0 ? (
          <p className="text-xs text-ink-soft dark:text-white/50 py-2">キャンセル案件はありません。</p>
        ) : (
          <ul className="space-y-1.5">
            {cancelled.slice(0, 20).map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 text-sm py-1 border-t border-[var(--color-border)] first:border-0">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-black tabular-nums px-2 py-0.5 rounded-md bg-red-500 text-white shrink-0">{caseNoLabel(c.case_no)}</span>
                  <span className="truncate text-ink-soft dark:text-white/70">{(c.prefecture || "") + " " + (c.city || "")} {c.ai_model_guess || ""}</span>
                </span>
                <span className="text-[11px] text-ink-soft dark:text-white/40 shrink-0">{new Date(c.created_at).toLocaleDateString("ja-JP")}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* info@ 問い合わせ窓口の導線 */}
      <section className="rounded-xl border border-[var(--color-border)] p-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Mail size={16} className="text-brand-500" /><h3 className="text-sm font-bold">問い合わせ窓口</h3></div>
          <p className="text-xs text-ink-soft dark:text-white/60 mt-1">顧客・業者からの問い合わせは {INFO_EMAIL} で受付。</p>
        </div>
        <a href={`mailto:${INFO_EMAIL}`} className="px-3 py-1.5 rounded-lg bg-brand-500 text-white text-sm font-semibold shrink-0">
          メール作成
        </a>
      </section>

      <p className="text-[11px] text-ink-soft dark:text-white/50">
        ※手数料は一律20%での推定。確定値は決済(収納代行)連携後に反映。
      </p>
    </div>
  );
}

/** 管理番号の表示（No.0001）。 */
function caseNoLabel(n: number | null): string {
  return n == null ? "No.----" : "No." + String(n).padStart(4, "0");
}

/** 案件をステータス別に件数集計。 */
function countByStatus(cases: CaseRow[]): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const c of cases) acc[c.status] = (acc[c.status] ?? 0) + 1;
  return acc;
}

/** 直近 n ヶ月の手数料（プラットフォーム売上）月次系列。成約案件を created_at の月で集計。 */
function monthlyFeeSeries(awarded: CaseRow[], n: number): Array<{ label: string; ym: string; value: number }> {
  const now = new Date();
  const buckets: Array<{ label: string; ym: string; value: number }> = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    buckets.push({ label: `${d.getMonth() + 1}月`, ym, value: 0 });
  }
  const idx: Record<string, number> = {};
  buckets.forEach((b, i) => (idx[b.ym] = i));
  for (const c of awarded) {
    if (!c.created_at) continue;
    const d = new Date(c.created_at);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const j = idx[ym];
    if (j !== undefined) buckets[j]!.value += calcFee(c.awarded_amount ?? 0);
  }
  return buckets;
}

function MonthlyRevenueChart({ series }: { series: Array<{ label: string; value: number }> }) {
  const W = 320, H = 160, padL = 8, padR = 8, padT = 16, padB = 24;
  const max = Math.max(1, ...series.map((s) => s.value));
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const bw = innerW / series.length;
  const total = series.reduce((s, x) => s + x.value, 0);
  const fmt = (v: number) => (v >= 10000 ? `¥${Math.round(v / 1000)}k` : `¥${v.toLocaleString("ja-JP")}`);

  return (
    <div className="rounded-xl border border-[var(--color-border)] p-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold">月次売上（手数料）推移</h3>
        <span className="text-[11px] text-ink-soft dark:text-white/50">直近6ヶ月</span>
      </div>
      {total === 0 ? (
        <p className="text-xs text-ink-soft dark:text-white/50 py-6 text-center">
          成約・決済データがまだないため、推移はこれから蓄積されます。
        </p>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="月次売上推移">
          <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke="var(--color-border)" />
          {series.map((s, i) => {
            const h = (s.value / max) * innerH;
            const x = padL + i * bw + bw * 0.2;
            const w = bw * 0.6;
            const y = padT + innerH - h;
            return (
              <g key={i}>
                {s.value > 0 && (
                  <text x={x + w / 2} y={y - 4} textAnchor="middle" fontSize="9" fill="#1CA0E8" fontWeight="bold">{fmt(s.value)}</text>
                )}
                <rect x={x} y={y} width={w} height={Math.max(h, 1)} rx="3" fill="#1CA0E8" />
                <text x={x + w / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="currentColor" opacity="0.6">{s.label}</text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function Stat({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-brand-400 bg-brand-50/60 dark:bg-brand-800/20" : "border-[var(--color-border)]"}`}>
      <div className="flex items-center gap-2 text-ink-soft dark:text-white/60 text-xs">{icon}<span>{label}</span></div>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}
